import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Viewer } from '@photo-sphere-viewer/core';
import { MarkersPlugin } from '@photo-sphere-viewer/markers-plugin';
import '@photo-sphere-viewer/core/index.css';
import '@photo-sphere-viewer/markers-plugin/index.css';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { equipmentISOStatus, getEquipmentStatusColorByStatus, markerColorByStatus } from 'data/data';
import { getEquipmentLocationMapByMapId } from 'components/axios/equipmentZoneAxios';
import { useEnumApi } from 'contexts/EnumApiContext';
import { useNavigate } from 'react-router-dom';
import "./marker.css";
import RightAsideCard from './RightAsideCard';
import { ImageMarkerConnectLine } from './ImageMarkerConnectLine';

const INTERVAL_TIME = 3000;

export function CompanyPanoViewer({ fileUrl, mapsId, zoneId, zoneLocationMap, equipments, latestLogs }) {
  const { getEnumValueByEnumNameAndCode } = useEnumApi();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const containerRef = useRef(null);
  const psvContainerRef = useRef(null);
  const viewerRef = useRef(null);
  const isMountedRef = useRef(true);

  const [equipmentMappingList, setEquipmentMappingList] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);

  const [isAutoRolling, setIsAutoRolling] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeArrowColor, setActiveArrowColor] = useState(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // [수정 1] 안전한 툴팁 표출 함수 (PSV 내부 마커 등록 확인)
  const safeShowTooltip = (markersPlugin, markerId) => {
    if (!isMountedRef.current || !viewerRef.current || !markersPlugin) return;
    try {
      const marker = markersPlugin.getMarker(markerId);
      if (marker) {
        markersPlugin.showMarkerTooltip(markerId);
      }
    } catch (e) {
      console.warn('Tooltip show error ignored:', e);
    }
  };

  // [수정 2] 선택 마커 적용 함수 안전성 강화
  const applySelectedMarker = (marker) => {
    if (!isMountedRef.current || !marker || !viewerRef.current) {
      setActiveMarker(null);
      setSelectedId(null);
      return;
    }

    setActiveMarker(marker);
    setSelectedId(marker.equipment?.id || null);
    const statusColor = getEquipmentStatusColorByStatus(marker.equipment?.status);
    setActiveArrowColor(statusColor?.bg2);

    const viewer = viewerRef.current;
    const markersPlugin = viewer.getPlugin(MarkersPlugin);

    if (!markersPlugin) return;

    try {
      markersPlugin.hideAllTooltips();
    } catch (e) {}

    // 마커 강조 클래스 적용 (.psv-marker--selected)
    setTimeout(() => {
      if (!isMountedRef.current || !viewerRef.current) return;
      try {
        document.querySelectorAll('.psv-marker').forEach((el) => {
          el.classList.remove('psv-marker--selected');
        });
        
        const currentMarker = markersPlugin.getMarker(marker.mId);
        if (currentMarker && currentMarker.element) {
          const markerEl = currentMarker.element;
          markerEl.classList.add('psv-marker--selected');
          
          // 마커 상태 색상을 CSS 변수로 전달 (아우라 파동 색상 동기화)
          const mainColor = statusColor?.bg2 || '#2672f7';
          markerEl.style.setProperty('--marker-status-color', mainColor);
        }
      } catch (e) {}
    }, 80);

    const targetYaw = parseFloat(marker.yawOrX);
    const targetPitch = parseFloat(marker.pitchOrY);

    if (!isNaN(targetYaw) && !isNaN(targetPitch)) {
      viewer
        .animate({
          yaw: targetYaw,
          pitch: targetPitch,
          speed: 600,
        })
        .then(() => {
          if (isMountedRef.current) {
            safeShowTooltip(markersPlugin, marker.mId);
            window.dispatchEvent(new Event('resize'));
          }
        })
        .catch(() => {
          if (isMountedRef.current) {
            safeShowTooltip(markersPlugin, marker.mId);
            window.dispatchEvent(new Event('resize'));
          }
        });
    } else {
      safeShowTooltip(markersPlugin, marker.mId);
      window.dispatchEvent(new Event('resize'));
    }
  };

  // 1. API 데이터 로드
  useEffect(() => {
    if (!mapsId) return;

    getEquipmentLocationMapByMapId(mapsId).then((res) => {
      if (!isMountedRef.current) return;

      if (res && res.coordinates) {
        let mappingList = [];
        res.coordinates.forEach((m) => {
          let markerId = `marker-${m.equipment?.id}`;
          const data = equipments?.find((e) => e.id === m.equipment?.id);
          mappingList.push({
            ...m,
            mId: markerId,
            equipment: data ? { ...data } : null,
          });
        });

        setEquipmentMappingList(mappingList);
        setCurrentIndex(0);
      } else {
        setEquipmentMappingList(null);
      }
    });
  }, [zoneId, mapsId, equipments, latestLogs]);

  // 2. 자동 롤링 타이머
  useEffect(() => {
    if (!equipmentMappingList || equipmentMappingList.length === 0) return;

    if (equipmentMappingList.length === 1) {
      applySelectedMarker(equipmentMappingList[0]);
      return;
    }

    if (!isAutoRolling) return;

    applySelectedMarker(equipmentMappingList[currentIndex]);

    const interval = setInterval(() => {
      if (!isMountedRef.current) return;

      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % equipmentMappingList.length;
        applySelectedMarker(equipmentMappingList[nextIndex]);
        return nextIndex;
      });
    }, INTERVAL_TIME);

    return () => clearInterval(interval);
  }, [equipmentMappingList, isAutoRolling, currentIndex]);

  // 3. PSV 뷰어 초기화
  useLayoutEffect(() => {
    if (!psvContainerRef.current) return;

    const viewer = new Viewer({
      container: psvContainerRef.current,
      panorama: fileUrl,
      plugins: [[MarkersPlugin, {}]],
    });

    viewerRef.current = viewer;

    const handleViewChange = () => {
      window.dispatchEvent(new Event('resize'));
    };

    viewer.addEventListener('position-updated', handleViewChange);
    viewer.addEventListener('zoom-updated', handleViewChange);

    return () => {
      if (viewerRef.current) {
        try {
          viewerRef.current.removeEventListener('position-updated', handleViewChange);
          viewerRef.current.removeEventListener('zoom-updated', handleViewChange);
          viewerRef.current.stopAnimation();
          const markersPlugin = viewerRef.current.getPlugin(MarkersPlugin);
          if (markersPlugin) {
            markersPlugin.clearMarkers();
          }
        } catch (e) {}
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, []);

  // [수정 3] PSV 마커 생성 Effect (의존성 배열 최소화)
  useEffect(() => {
    if (!viewerRef.current || !equipmentMappingList) return;

    const viewer = viewerRef.current;
    const markersPlugin = viewer.getPlugin(MarkersPlugin);

    if (!markersPlugin) return;

    try {
      markersPlugin.clearMarkers();
    } catch (e) {}

    equipmentMappingList.forEach((m) => {
      const statusColor = getEquipmentStatusColorByStatus(m.equipment?.status);
      const isoStatus = equipmentISOStatus.find((iso) => iso.code === m.equipment?.lowestDiagnosisRank);
      const isoColor = isoStatus?.color3 || '#62b929';
      const isoCode = (isoStatus || { code: '-' }).code;

      markersPlugin.addMarker({
        id: m.mId,
        position: { yaw: m.yawOrX, pitch: m.pitchOrY },
        image: markerColorByStatus[m.equipment?.status || 'unmapped'],
        size: { width: 32, height: 32 },
        anchor: 'bottom center',
        style: {
          cursor: 'pointer',
        },
        tooltip: {
          trigger: 'manual',
          position: 'top center',
          content: `
            <div class="map_pin" style="width: 300px; display: flex; justify-content: center;">
              <div class="map_pin_card" style="
                width: 300px; 
                padding: 12px 16px; 
                border-radius: 14px; 
                color: #fff; 
                font-size: 13px; 
                display: flex; 
                flex-direction: column; 
                border: 2px solid ${statusColor?.bg2 ?? 'rgba(255,255,255,0.2)'}; 
                gap: 10px; 
                background: rgba(38, 39, 42, 0.95); 
                backdrop-filter: blur(6px); 
                box-shadow: 0 0 15px ${statusColor?.bg2 ?? 'rgba(0,0,0,0.4)'};
              ">
                <div style="border-radius: 16px; width: 100%; background: ${statusColor?.bg2 ?? '#777'}; color: ${statusColor?.color ?? '#fff'}; padding: 6px; text-align: center; font-weight: bold; font-size: 13px; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
                  ${m.equipment ? `${m.equipment.name} (${m.equipment.equipmentNo})` : t('message.common.text.Unmapped')}
                </div>

                <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 4px;">
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span style="color: #fff; font-size: 12px;">${t('message.common.subtitle.equipment.level3')}</span>
                    <span style="font-weight: 500; font-size: 11px;">${m.equipment?.equipmentCtgr?.name ?? '-'}</span>
                  </div>

                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span style="color: #fff; font-size: 12px;">${t('message.common.subtitle.status')}</span>
                    <span style="background: ${statusColor?.bg2 ?? '#777'}; color: ${statusColor?.color ?? '#fff'}; padding: 2px 10px; border-radius: 12px; font-size: 11px; font-weight: bold;">
                      ${getEnumValueByEnumNameAndCode('EquipmentFaultStatus', m.equipment?.status)?.[0]?.title ?? '-'}
                    </span>
                  </div>

                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span style="color: #fff; font-size: 12px;">${t('message.common.subtitle.operation.info')}</span>
                    <span style="background: ${m.equipment?.latestEquipmentOp?.opStatus?.toUpperCase() === 'ACTIVE' ? '#2672f7' : '#777'}; padding: 2px 10px; border-radius: 12px; font-size: 11px;">
                      ${m.equipment?.latestEquipmentOp?.opStatus?.toUpperCase() === 'ACTIVE' ? t('message.common.text.active') : t('message.common.text.stop')}
                    </span>
                  </div>

                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span style="color: #fff; font-size: 12px;">${t('message.common.subtitle.iso')} ${t('message.common.subtitle.grade')}</span>
                    <span style="background: ${isoColor}; color: ${isoStatus?.fontColor ?? '#fff'}; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: bold;">
                      ${isoCode}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          `,
        },
      });
    });

    // 100ms 지연 후 첫 번째 마커 선택
    const timer = setTimeout(() => {
      if (isMountedRef.current && equipmentMappingList.length > 0) {
        applySelectedMarker(equipmentMappingList[0]);
      }
    }, 100);

    const handleSelectMarker = (e) => {
      const targetMarker = e.marker;
      if (!targetMarker) return;

      const foundIndex = equipmentMappingList.findIndex((item) => item.mId === targetMarker.id);
      if (foundIndex !== -1) {
        if (equipmentMappingList.length === 1) {
          applySelectedMarker(equipmentMappingList[0]);
          return;
        }

        if (foundIndex === currentIndex && !isAutoRolling) {
          setIsAutoRolling(true);
        } else {
          setIsAutoRolling(false);
          setCurrentIndex(foundIndex);
          applySelectedMarker(equipmentMappingList[foundIndex]);
        }
      }
    };

    const handleClickBackground = () => {
      if (equipmentMappingList.length > 1 && !isAutoRolling) {
        setIsAutoRolling(true);
      }
    };

    markersPlugin.addEventListener('select-marker', handleSelectMarker);
    viewer.addEventListener('click', handleClickBackground);

    return () => {
      clearTimeout(timer);
      try {
        markersPlugin.removeEventListener('select-marker', handleSelectMarker);
        if (viewerRef.current) {
          viewerRef.current.removeEventListener('click', handleClickBackground);
        }
      } catch (e) {}
    };
  }, [equipmentMappingList]); // ⚡ 의존성 배열에서 currentIndex, isAutoRolling 제거!

  const lineActiveMarker = activeMarker
    ? {
        ...activeMarker,
        mId: `psv-marker-${activeMarker.mId}`,
      }
    : null;
  
  return (
    <React.Fragment>
      <style>{`
        .psv-container {
          z-index: 10 !important;
        }

        .psv-tooltip {
          z-index: 99999 !important;
          display: flex !important;
          justify-content: center !important;
          margin-top: -10px !important;
        }

        .psv-tooltip-content {
          padding: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
          border: none !important;
        }

        .pano-viewer-layout {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          gap: 16px;
          width: 100%;
          min-width: 0;
        }

        .pano-viewer-main {
          flex: 1 1 0;
          min-width: 0;
        }

        .pano-viewer-layout .right-aside-card {
          flex: 0 0 auto;
        }

        /* 툴팁 카드 기본 설정 */
        .psv-tooltip .map_pin_card {
          transition: transform 0.2s;
        }

        /* 모바일 */
        @media (max-width: 767px) {
          .pano-viewer-layout {
            flex-direction: column;
            gap: 12px;
          }

          .pano-viewer-main {
            width: 100%;
            min-width: 0;
          }

          .pano-viewer-layout .right-aside-card {
            width: 100%;
          }
          
          .psv-tooltip {
            max-width: calc(100vw - 24px) !important;
            box-sizing: border-box !important;
            margin-top: 0 !important;
          }

          /* 마커 위쪽 */
          .psv-tooltip--top-center .map_pin_card {
            transform: translate(-20px, 10px) !important;
          }

          /* 마커 아래쪽 */
          .psv-tooltip--bottom-center .map_pin_card {
            transform: translate(-20px, -13px) !important;
          }
            
          .psv-tooltip-content {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .map_pin {
            width: min(230px, calc(100vw - 40px)) !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .map_pin_card {
            width: 100% !important;
            padding: 8px 10px !important;
            gap: 6px !important;
            border-radius: 11px !important;
            box-sizing: border-box !important;
          }

          .map_pin_card > div:first-child {
            padding: 4px !important;
            font-size: 11px !important;
          }

          .map_pin_card > div:nth-child(2) {
            gap: 5px !important;
          }
        }
      `}</style>

      <div
        ref={containerRef}
        className="pano-viewer-layout"
        style={{
          position: 'relative',
          // display: 'flex',
          // gap: '16px',
          padding: '10px',
          color: '#e5e7eb',
          // width: '100%',
        }}
      >
        <ImageMarkerConnectLine
          activeMarker={lineActiveMarker}
          containerRef={containerRef}
          arrowColor={activeArrowColor}
        />
        
        <div className="pano-viewer-main" style={{ background: '#26272a', borderRadius: '12px', padding: '10px', width: '100%', position: 'relative', zIndex: 1 }}>
          <div style={{ width: '100%', height: '565px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div ref={psvContainerRef} style={{ width: '100%', flex: 1, borderRadius: '8px', overflow: 'visible', position: 'relative' }} />
            {zoneLocationMap?.length > 1 && (
              <div style={{ display: 'flex', gap: '6px', padding: '0 4px', flexShrink: 0 }}>
                {zoneLocationMap?.map((map, bi) => (
                  <Button
                    key={map.id}
                    variant={map.id == mapsId ? 'contained' : 'outlined'}
                    color="primary"
                    size="small"
                    onClick={() => {
                      navigate(`/dashboard/sub?zId=${zoneId}&mId=${map.id}`);
                    }}
                  >
                    {t('message.common.subtitle.equipmentPart.image')}{bi + 1}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="right-aside-card" style={{ position: 'relative', zIndex: 1 }}>
          <RightAsideCard equipments={equipments} latestLogs={latestLogs} selectedId={selectedId} />
        </div>
      </div>
    </React.Fragment>
  );
}

export default CompanyPanoViewer;